/**
 * Collapse a burst of identical cache invalidations into one.
 *
 * A bulk file writes N cells through N calls of the SAME per-cell endpoint, and
 * every one of them used to invalidate the whole roster document -- so a
 * 40-cell sweep refetched a document of forty rows and six months forty times.
 *
 * ⛔ THIS IS NOT A CONCURRENCY CHANGE, AND MUST NEVER BECOME ONE. The writes in
 * `Schedule.fileSelection` are sequential ON PURPOSE: each POST commits and
 * RE-DERIVES that day's code stack server-side, so overlapping them would race
 * on the derivation and corrupt the stack -- silently, and with no test in this
 * repo that would notice. Nothing here touches the order or the overlap of the
 * requests; it only decides WHEN the cache is told they happened. The requests
 * stay exactly as sequential as whoever calls them made them.
 *
 * THE FAILURE MODE IS A WASTED REFETCH, NEVER A WRONG DOCUMENT. The window is a
 * heuristic: if a gap between two writes ever exceeds it, the batch is split and
 * you get two refetches instead of one. That is the whole cost of being wrong.
 * It cannot drop an invalidation -- `flush()` on teardown guarantees a scheduled
 * one still runs -- and it cannot reorder or lose a write, because it never sees
 * the writes at all.
 *
 * `busy` IS WHY THE WINDOW CAN BE SHORT. A fixed debounce has to be long enough
 * to cover the slowest gap between two writes, and on a slow link that is the
 * whole round trip -- so it would have to be seconds, which would make a single
 * cell edit feel broken. Asking whether a write is still in flight moves the
 * network out of the window entirely: while anything is running the timer just
 * defers again, so the window only has to cover the JS gap BETWEEN two
 * requests, which is milliseconds. See `ROSTER_INVALIDATE_MS`.
 */

/**
 * How long the batch may go idle before the invalidation is believed to be the
 * last one. Only ever spans the gap between two writes, never a request -- see
 * `busy` in the module note -- so this is sized against a React re-render of the
 * schedule grid, not against the network.
 */
export const ROSTER_INVALIDATE_MS = 150

export interface TrailingInvalidator {
  /** A write landed. Runs `invalidate` once the burst goes quiet. */
  schedule(): void
  /**
   * Run a pending invalidation NOW, and resolve once it has settled.
   * Idempotent; a no-op that resolves immediately when none is due.
   *
   * The returned promise is what a "wait for the seed to be safe again" hangs on: `invalidate`'s own promise (a real refetch, in
   * `useFileShift`) is threaded straight through, so a caller awaiting
   * `flush()` is awaiting the GET landing, not just the timer clearing.
   */
  flush(): Promise<void>
  /** True while an invalidation is owed. Exists for the test and for teardown. */
  pending(): boolean
}

/**
 * @param invalidate what to do once, after the burst. May return a promise;
 *   `flush()` resolves once it does.
 * @param busy is a write still in flight? While true the window keeps deferring.
 * @param delayMs idle gap that ends the burst.
 */
export function createTrailingInvalidator(
  invalidate: () => void | Promise<void>,
  busy: () => boolean = () => false,
  delayMs: number = ROSTER_INVALIDATE_MS,
): TrailingInvalidator {
  let timer: ReturnType<typeof setTimeout> | null = null

  function arm() {
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(tick, delayMs)
  }

  function tick() {
    timer = null
    // Another cell is mid-flight, so this was not the last one after all. Re-arm
    // rather than invalidate: firing here is exactly the wasted refetch this
    // exists to remove, and the write still running will schedule again anyway.
    if (busy()) {
      arm()
      return
    }
    invalidate()
  }

  return {
    schedule: arm,
    flush() {
      if (timer === null) return Promise.resolve()
      clearTimeout(timer)
      timer = null
      // ⛔ CLEARED **AND** RUN, not just cleared. Dropping a pending timer on
      // teardown would mean the last cell of a batch never invalidated at all,
      // and the next reader would be served a document the cache believes is
      // fresh and which is missing the write that just succeeded. A stale roster
      // is a worse bug than the N refetches this file removes.
      return Promise.resolve(invalidate())
    },
    pending: () => timer !== null,
  }
}
